import { TokenAmount, Pair, Currency } from '@pancakeswap/sdk'
import { useMemo, useEffect, useState } from 'react'
import IPancakePairABI from 'config/abi/IPancakePair.json'
import { Interface } from '@ethersproject/abi'
import { useNetwork } from 'wagmi'
import { customTokenList, getChainId } from 'config/constants/exchange'
import { useMultipleContractSingleData } from '../state/multicall/hooks'
import { wrappedCurrency } from '../utils/wrappedCurrency'
import { getRouterContractv1 } from 'utils/exchange'
import { provider } from 'utils/wagmi'

const PAIR_INTERFACE = new Interface(IPancakePairABI)
const VIRTUAL_BASE = BigInt('1000000000000000000000')
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

export enum PairState {
  LOADING,
  NOT_EXISTS,
  EXISTS,
  INVALID,
}

// ─── Module-level price cache ─────────────────────────────────────────────────
// Prices are fetched once per chainId and reused across all usePairs calls.
// { chainId -> { tokenAddress -> price as bigint } }
const priceCache: Record<number, Record<string, bigint>> = {}
let isFetching = false

const priceListeners: Set<() => void> = new Set()

const notifyListeners = () => priceListeners.forEach((fn) => fn())

async function fetchAllPrices(chainId: number) {
  if (isFetching) return
  if (priceCache[chainId]) return // already fetched

  isFetching = true
  try {
    const tokens = customTokenList[chainId]
    if (!tokens?.length) return

    const router = getRouterContractv1(chainId, provider(chainId))

    const results = await Promise.all(
      tokens.map((token) =>
        router
          .getPrice(token.address)
          .then((p: any) => ({ address: token.address.toLowerCase(), price: BigInt(p.toString()) }))
          .catch(() => ({ address: token.address.toLowerCase(), price: undefined })),
      ),
    )

    priceCache[chainId] = {}
    results.forEach(({ address, price }) => {
      if (price !== undefined) priceCache[chainId][address] = price
    })

    notifyListeners()
  } finally {
    isFetching = false
  }
}

// Single hook — reads from cache, triggers fetch only if cache is empty
function useCustomTokenPrices(chainId: number): Record<string, bigint> {
  const [, forceRender] = useState(0)

  useEffect(() => {
    const refresh = () => forceRender((n) => n + 1)
    priceListeners.add(refresh)

    // Fetch only if not already cached
    if (!priceCache[chainId]) {
      fetchAllPrices(chainId)
    }

    return () => {
      priceListeners.delete(refresh)
    }
  }, [chainId])

  return priceCache[chainId] ?? {}
}

// ─────────────────────────────────────────────────────────────────────────────

const isCustomToken = (token: any, chainId: number): boolean => {
  if (!token || !chainId) return false
  return (
    customTokenList[chainId]?.some(
      (t) => t.address.toLowerCase() === token.address.toLowerCase(),
    ) ?? false
  )
}

export function usePairs(
  currencies: [Currency | undefined, Currency | undefined][],
): [PairState, Pair | null][] {
  const { chain } = useNetwork()
  const chainId = getChainId(chain)

  // One call, cached — never re-fetches unless cache is empty
  const prices = useCustomTokenPrices(chainId)
  const pricesReady = Object.keys(prices).length > 0

  const tokens = useMemo(
    () =>
      currencies.map(([currencyA, currencyB]) => [
        wrappedCurrency(currencyA, chainId),
        wrappedCurrency(currencyB, chainId),
      ]),
    [chainId, currencies],
  )

  const pairAddresses = useMemo(
    () =>
      tokens.map(([tokenA, tokenB]) => {
        try {
          if (!tokenA || !tokenB || tokenA.equals(tokenB)) return undefined

          if (isCustomToken(tokenA, chainId) || isCustomToken(tokenB, chainId)) {
            return ZERO_ADDRESS
          }

          return Pair.getAddress(tokenA, tokenB)
        } catch (error: any) {
          console.error(
            error.msg,
            `- pairAddresses: ${tokenA?.address}-${tokenB?.address}`,
            `chainId: ${tokenA?.chainId}`,
          )
          return undefined
        }
      }),
    [tokens, chainId],
  )

  const results = useMultipleContractSingleData(pairAddresses, PAIR_INTERFACE, 'getReserves')

  return useMemo(() => {
    return results.map((result, i) => {
      const { result: reserves, loading } = result
      const tokenA = tokens[i][0]
      const tokenB = tokens[i][1]

      if (loading) return [PairState.LOADING, null]
      if (!tokenA || !tokenB || tokenA.equals(tokenB)) return [PairState.INVALID, null]

      // ── Custom pair ────────────────────────────────────────────────────────
      if (isCustomToken(tokenA, chainId) || isCustomToken(tokenB, chainId)) {
        if (!pricesReady) return [PairState.LOADING, null]

        const priceA = prices[tokenA.address.toLowerCase()]
        const priceB = prices[tokenB.address.toLowerCase()]

        if (!priceA || !priceB) return [PairState.NOT_EXISTS, null]

        const [token0, token1] = tokenA.sortsBefore(tokenB)
          ? [tokenA, tokenB]
          : [tokenB, tokenA]

        const [p0, p1] = tokenA.sortsBefore(tokenB)
          ? [priceA, priceB]
          : [priceB, priceA]

        const reserve0 = VIRTUAL_BASE * BigInt(10 ** token0.decimals)

        const decimalDiff = token1.decimals - token0.decimals
        const reserve1 =
          decimalDiff >= 0
            ? (reserve0 * p0 * BigInt(10 ** decimalDiff)) / p1
            : (reserve0 * p0) / (p1 * BigInt(10 ** Math.abs(decimalDiff)))

        if (reserve0 === BigInt(0) || reserve1 === BigInt(0)) {
          return [PairState.NOT_EXISTS, null]
        }

        try {
          return [
            PairState.EXISTS,
            new Pair(
              new TokenAmount(token0, reserve0.toString()),
              new TokenAmount(token1, reserve1.toString()),
            ),
          ]
        } catch {
          return [PairState.INVALID, null]
        }
      }

      // ── Normal PancakeSwap pair ────────────────────────────────────────────
      if (!reserves) return [PairState.NOT_EXISTS, null]
      const { reserve0, reserve1 } = reserves
      const [token0, token1] = tokenA.sortsBefore(tokenB)
        ? [tokenA, tokenB]
        : [tokenB, tokenA]

      return [
        PairState.EXISTS,
        new Pair(
          new TokenAmount(token0, reserve0.toString()),
          new TokenAmount(token1, reserve1.toString()),
        ),
      ]
    })
  }, [results, tokens, chainId, prices, pricesReady])
}

export function usePair(tokenA?: Currency, tokenB?: Currency): [PairState, Pair | null] {
  const pairCurrencies = useMemo<[Currency, Currency][]>(
    () => [[tokenA, tokenB]],
    [tokenA, tokenB],
  )
  return usePairs(pairCurrencies)[0]
}